/**
 * CoExecutorBanner – Shows other executors working on the same estate.
 * Displayed at the top of settlement screens when multiple executors exist.
 */

import type { CoExecutor } from '../../contexts/SettlementContext';

interface CoExecutorBannerProps {
  executors: CoExecutor[];
  className?: string;
}

export default function CoExecutorBanner({ executors, className = '' }: CoExecutorBannerProps) {
  if (executors.length === 0) return null;

  return (
    <div className={`bg-blue-100 border border-blue-300 rounded-xl p-4 ${className}`}>
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-blue-300/30 flex items-center justify-center shrink-0">
          <svg className="w-4 h-4 text-blue-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-blue-900">
            {executors.length} other executor{executors.length !== 1 ? 's' : ''} on this estate
          </h3>
          <p className="text-xs text-blue-700 mt-0.5">
            Progress is shared — changes made by any executor are visible to all.
          </p>
          <div className="flex flex-wrap gap-2 mt-2">
            {executors.map((ex) => (
              <span
                key={ex.userId}
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-blue-100 text-xs font-medium text-blue-800"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                {ex.name || ex.email}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
